/* Copyright (c) 2017 Backpack Health LLC */
import React, { ReactNode } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'lodash';
import { Redirect } from 'react-router';

import { getLoggedInUser, getActiveProfile, getActiveProfileId, getMainProfileId } from '../../redux/reduxSelectors';
import ProfileActions from '../../redux/ProfileActions';
import { actions as systemNotificationActions } from '../../../redux/ducks/systemNotification';
import ProfilePhrases from '../../../../phraseKeys/Profile.phrase';
import { actions as userActions } from '../../../redux/ducks/user';
import { selectors as actionItemSelectors, actions as actionItemActions } from '../../../redux/ducks/actionItems';
import { selectors as listSelectors } from '../../../redux/ducks/lists/lists';
import { actions as systemModalActions } from '../../../redux/ducks/systemModal';
import CommunicationOptionsSetup from '../communicationOptions/CommunicationOptionsSetup';
import VerifyEmail from '../verifyEmail/VerifyEmail';
import { ActionItem, ActionItemViewName } from '../../../../models/ActionItemModel';
import PrivacyIntro from '../../../appUnauthorized/views/PrivacyIntro/PrivacyIntro';
import AccountPrivacyConsent from '../../views/AccountPrivacyConsent';
import UserModel from '../../../../models/UserModel';
import NavigationActions from '../../redux/NavigationActions';
import RouteNames from '../../RouteNames';
import userApi from '../../../../apiServices/UserService';
import AppUtils from '../../../../utilities/AppUtils';
import DoNotConsentModal, { WithdrawName } from '../../../components/modal/DoNotConsentModal';
import ChangeEmail from '../../../views/ChangeEmail';
import SelfOwnerSelection from './ViewAcceptOwnProfile';

function mapStateToProps(state, routerProps) {
  const profile = getActiveProfile(state);
  const isMainProfile = getActiveProfileId(state) === getMainProfileId(state);
  const genderCollection = listSelectors.getGenderCollection(state);

  const genderIdMap = {};
  genderCollection.forEach((g) => {
    genderIdMap[g.id] = g;
  });

  const user = getLoggedInUser(state);

  return {
    user,
    userActionItems: actionItemSelectors.getUserActionItems(state, user.data.id),
    isNewUser: routerProps.location.pathname === RouteNames.newUserSetup(),

    hasJoinGroupIntent: user.hasJoinGroupIntent(),
    profile,
    phraseData: ProfilePhrases.myProfile(isMainProfile, profile.greetingName()),
    currentColorTheme: listSelectors.getCurrentColorTheme(state),
    profileActionItems: actionItemSelectors.getProfileActionItems(state, profile.data.id),
    genderIdMap,
    submitProfileChanges: (profileData) => {
      ProfileActions.api.updateProfile(profileData);
    },
    requestActiveProfile: (callback) => {
      ProfileActions.api.getProfile(profile.data.id, callback);
    },
    onAccept: (profileId, policyIds, callback) => {
      ProfileActions.api.provideConsent(profileId, policyIds).then(callback);
    },
    goHome: () => {
      NavigationActions.replace(routerProps.history, RouteNames.home());
    },
    goToNewProfileSetup: () => {
      NavigationActions.replace(routerProps.history, RouteNames.newProfileSetup());
    },
  };
}

function mapDispatchToProps(dispatch) {
  const r = bindActionCreators({
    showNotification: systemNotificationActions.showNotification,
    dismissNotification: systemNotificationActions.dismissNotification,
  }, dispatch);

  return {
    ...r,
    clearIntent: (userData, cb) => {
      dispatch(userActions.submitUserUpdate(
        {
          ...userData,
          intent: null,
        },
        cb,
      ));
    },
    refreshActionItems: (userId, profileId, callback) => {
      dispatch(actionItemActions.requestActionItems(userId, profileId)).then(callback);
    },
    showSystemModal: (content) => {
      dispatch(systemModalActions.show(content));
    },
    onAccept: (userId, policyIds, callback) => {
      userApi.provideConsent(userId, policyIds).then(callback);
    },
    onRequestEmailChange: (userId, email, callback) => {
      dispatch(userActions.requestEmailChange(userId, email, callback));
    },
  };
}

const userSetupStepOrder = [
  ActionItemViewName.policy_user_account,
  ActionItemViewName.policy_user_email,
  ActionItemViewName.policy_communication,
  ActionItemViewName.email_verification,
  ActionItemViewName.self_owner_selection,
];

interface State {
  stepIndex: number;
  stepName: string;
  actionItemSteps: ActionItem[];
}

interface Props {
  userActionItems: ActionItem[];
  user: UserModel;
  refreshActionItems(userId: number, profileId: number, callback): void;
  onAccept(userId: number, profileIds: number[], callback): void;
  goHome(): void;
  isNewUser: boolean;
  showSystemModal(modal: ReactNode): void;
  goToNewProfileSetup(): void;
  onRequestEmailChange(id, email, callback): void;
}

class UserSetup extends React.Component {
  props: Props;
  state: State = {
    stepIndex: 0,
    stepName: '',
    actionItemSteps: [],
  };

  UNSAFE_componentWillMount() {
    const actionItemSteps = [];
    userSetupStepOrder.forEach(name => {
      const actionItem = this.props.userActionItems.filter(a => a.view === name)[0];
      if (actionItem) {
        actionItemSteps.push(actionItem);
      }
    });

    this.setState({
      actionItemSteps,
      stepName: actionItemSteps.length ? actionItemSteps[0].view : '',
    });
  }

  onFinishStep = () => {
    const { user, isNewUser, goToNewProfileSetup, goHome, refreshActionItems } = this.props;
    const { stepIndex, actionItemSteps } = this.state;

    const nextStep = stepIndex + 1;

    if (nextStep >= actionItemSteps.length) {
      if (isNewUser) {
        refreshActionItems(user.data.id, user.data.profile_id, goToNewProfileSetup);
      } else {
        refreshActionItems(user.data.id, user.data.profile_id, goHome);
      }
    } else {
      this.setState({
        stepIndex: nextStep,
        stepName: actionItemSteps[nextStep].view,
      });
    }
  }

  onAcceptStep = () => {
    const { user } = this.props;
    const { stepIndex, actionItemSteps } = this.state;
    const currentActionItem = actionItemSteps[stepIndex];

    this.props.onAccept(user.data.id, currentActionItem.policy_id, () => {
      this.onFinishStep();
    });
  }

  submitEmailChange = (email, callback) => {
    const { user } = this.props;
    const { stepIndex, actionItemSteps } = this.state;

    this.props.onRequestEmailChange(user.data.id, email, () => {
      this.setState({ stepName: actionItemSteps[stepIndex].view });
    });
  }

  render() {
    const { isNewUser, user } = this.props;
    const { stepIndex, actionItemSteps, stepName } = this.state;

    if (actionItemSteps.length === 0) { return <Redirect to={RouteNames.home()} />; }

    switch (stepName) {
      case ActionItemViewName.policy_user_account:
        return (
          <PrivacyIntro
            isNewUser={isNewUser}
            onAccept={this.onAcceptStep}
            onCancel={() => {
              AppUtils.logout({}, AppUtils.reloadApp);
            }}
            showLanguageControl={false}
          />
        );
      case ActionItemViewName.policy_user_email:
        return (
          <AccountPrivacyConsent
            onCancel={() => {
              AppUtils.logout({}, AppUtils.reloadApp);
            }}
            onAccept={this.onAcceptStep}
            onWithdrawConsent={(policyName) => {
              if (isNewUser) {
                this.props.showSystemModal(<DoNotConsentModal withdrawName={WithdrawName.duringSetup} policyName={policyName} isShared={false} />);
              } else {
                this.props.showSystemModal(<DoNotConsentModal withdrawName={WithdrawName.afterSetup} policyName={policyName} isShared={false} />);
              }
            }}
          />
        );
      case ActionItemViewName.policy_communication:
        return (
          <CommunicationOptionsSetup
            isNewUser={isNewUser}
            onFinish={this.onFinishStep}
            onCancel={() => {
              if (stepIndex === 0) {
                AppUtils.logout({}, AppUtils.reloadApp);
              } else {
                this.setState({ stepIndex: stepIndex - 1, stepName: actionItemSteps[stepIndex - 1].view });
              }
            }}
          />
        );
      case ActionItemViewName.email_verification:
        return (
          <VerifyEmail
            userData={user.data}
            onCancel={() => {
              AppUtils.logout({}, AppUtils.reloadApp);
            }}
            onChangeEmail={() => {
              this.setState({ stepName: 'change_email' });
            }}
          />
        );
      case ActionItemViewName.self_owner_selection:
        return (
          <SelfOwnerSelection />
        );
      case 'change_email':
        return (
          <ChangeEmail
            defaultEmail={user.data.email}
            goBack={() => {
              this.setState({ stepName: actionItemSteps[stepIndex].view });
            }}
            submitEmail={this.submitEmailChange}
          />
        );
      default:
        return null;
    }
  }
}
export default connect(mapStateToProps, mapDispatchToProps)(UserSetup);
